import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { Box } from '@mui/material'
import { Fragment } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { RunFailure, toastRunFailure } from './RunFailureDetails'
import { ScheduleProductionRequirement } from './ScheduleProductionRequirement.view'
import { ScheduleWorkstationDowntime } from './ScheduleWorkstationDowntime.view'
import { ScheduleCleaningMapping } from './ScheduleCleaningMapping.view'
import { ScheduleResourceSwitching } from './ScheduleResourceSwitching.view'
import { SchedulerResourceAndMaterialConstraintsView } from './ScheduleResourceAndMaterialConstraints.view'
import { SchedulerResourceCapacityProfileView } from './ScheduleResourceCapacityProfile.view'
import { ScheduleTanksView } from './ScheduleTanks.view'
import { ScheduleTankWeekStateView } from './ScheduleTankWeekState.view'
import { SchedulerResourceAndInputsView } from './ScheduleResourceAndInputs.view'
import { SchedulePackagingRoutesView } from './SchedulePackagingRoutes.view'
import { ScheduleResourceAndOutputMappingView } from './ScheduleResourceAndOutputMapping.view'
import { ScheduleProductCleanChangeover } from './ScheduleProductCleanChangeover.view'
import { ScheduleBaseMaterialCleanChangeover } from './ScheduleBaseMaterialCleanChangeover.view'
import { ScheduleSynchronizedLines } from './ScheduleSynchronizedLines.view'
import { ScheduleSORlikeView } from './ScheduleSORlikeView.view'
import { ScheduleKitchenCyclesView } from './ScheduleKitchenCycles.view'
import { ScheduleSteriliserRouting } from './ScheduleSteriliserRouting.view'
import { AssetUtilisationView } from './AssetUtilisation.view'
import { DailyUtilisationView } from './DailyUtilisation.view'
import { ConstraintUtilisationView } from './ConstraintUtilisation.view'
import { CrewUtilisationView } from './CrewUtilisation.view'
import { CleaningSummaryView } from './CleaningSummary.view'
import { DetailsView } from './Details.view'
import { FGUtilisationView } from './FGUtilisation.view'
import { InputUtilisationView } from './InputUtilisation.view'
import { KitchenUtilisationView } from './KitchenUtilisation.view'
import { LineUtilisationView } from './LineUtilisation.view'
import { RulesWorkbench } from './RulesWorkbench.view'
import {
  SchedulerRunConfig,
  useConfigurationSettings,
  useDataPrepare,
  JobFailureOrigin,
  usePlanConfigurationSettings,
  useRunSchedulerDirect,
  useScheduleName,
} from './Scheduler.data'
import { ConfigurationSettingsDialog } from './ConfigurationSettings.dialog'
import {
  HOURS_PER_WEEK,
  readStoredMultiWeek,
} from './ScheduleControlPanel.view'
import { SchedulerInputSidebar } from './SchedulerInput.sidebar'
import { SchedulerTopStrip } from './SchedulerInput.topStrip'
import {
  ScheduleVariantContext,
  DefaultScheduleVariantContext,
  defaultVariantOf,
  ScheduleVariantKey,
  ScheduleVariantSelection,
  SchedulerDataVariant,
  useScheduleVariants,
  useSchedulerData,
} from 'Features/SchedulerView/SchedulerView.data'
import { InputRow } from 'Features/SchedulerView/SchedulerView.types'
import { computeMakespan } from './makespan'
import {
  DataSnapshotsSection,
  CommentsSection,
} from './SchedulerInput.extraSections'
import {
  RULE_MANAGED,
  SECTIONS,
  isSectionHidden,
  sectionLabel,
} from './SchedulerInput.sections'
import { ManagedSourceBanner } from './ManagedSourceBanner'
import { SchedulerBrandBar } from './SchedulerBrandBar'
import {
  FONT_MONO,
  FONT_SANS,
  SchDisplay,
  SchChip,
  SchedulerThemeScope,
  TOKENS,
} from './SchedulerDesign'
import HomeIcon from '@mui/icons-material/Home'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import LockIcon from '@mui/icons-material/Lock'

type InputPageParams = {
  scheduleId: string
  iteration: string
}

// The scheduler plans an 8-week horizon, so the week selector always shows 1–8.
const WEEKS = [1, 2, 3, 4, 5, 6, 7, 8]

/**
 * Placeholder shown between switching tab and mounting the section — see
 * `DeferredSection` below. Plain centred label, nothing else: it is on screen
 * for a frame or two, so anything with more in it reads as a flash of
 * furniture rather than as a wait.
 */
const SectionLoading = ({ label }: { label?: string }) => (
  <Box
    aria-busy="true"
    aria-live="polite"
    sx={{
      height: '100%',
      // The two full-bleed branches give this a flex parent to fill; the
      // third does not, so without a floor it would centre inside its own
      // line height and sit at the top of the page.
      minHeight: 240,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: TOKENS.ink400,
      fontSize: 12,
      fontFamily: FONT_SANS,
    }}
  >
    {label ? `Loading ${label}…` : 'Loading…'}
  </Box>
)

/**
 * Paints the switched-to tab BEFORE mounting its contents.
 *
 * Selecting a section is a synchronous state update, so React renders the new
 * section in the same commit as the sidebar highlight — and the browser
 * cannot paint until that render finishes. The input sections are not small
 * (FactoryLayout is ~4.3k lines, SchedulePackagingRoutes ~2k, RulesWorkbench
 * ~1.8k), so the click appeared to hang on the old tab while the new one
 * built itself, then everything appeared at once.
 *
 * Splitting it in two fixes the perception without making anything faster:
 * the first render after a switch is this placeholder, which costs nothing,
 * so the new tab and its title appear immediately; the real section mounts on
 * the next animation frame and shows its own data loader from there.
 *
 * Keyed on the section, and `readyFor` is compared to that key rather than
 * held as a bare boolean — the page re-renders for plenty of reasons that are
 * not a tab change (week selection, run progress, a settings fetch), and a
 * boolean reset would unmount and rebuild the whole section on every one of
 * them. Comparing keys means an unrelated re-render passes straight through.
 */
const DeferredSection = ({
  sectionKey,
  label,
  children,
}: {
  sectionKey: string
  /** Section name, so the placeholder says what is arriving. */
  label?: string
  children: React.ReactNode
}) => {
  const [readyFor, setReadyFor] = useState<string | null>(null)

  useEffect(() => {
    // DOUBLE rAF, and the second one is the whole mechanism.
    //
    // An rAF callback runs BEFORE the paint of the frame it belongs to, so a
    // single one would schedule the heavy mount into the very frame that was
    // supposed to show the placeholder — React would re-render and the
    // browser would paint the finished section, having never shown the
    // loader. The first callback lets that frame paint the placeholder; the
    // second, one frame later, mounts the section.
    //
    // A single `setTimeout(0)` has the same defect for the same reason: the
    // macrotask can be serviced before the browser gets to paint.
    let inner = 0
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setReadyFor(sectionKey))
    })
    return () => {
      cancelAnimationFrame(outer)
      if (inner) cancelAnimationFrame(inner)
    }
  }, [sectionKey])

  if (readyFor === sectionKey) return <>{children}</>

  return <SectionLoading label={label} />
}

// Marks a failure the planner can FIX rather than retry, so the survey's
// failure handler knows not to fall through to a full run that would fail the
// same way with a worse message. Set by `PipelineError::InputData` in the API
// (`scheduler_api/src/pipeline.rs`, INPUT_DATA_PREFIX) — the two must match.
const INPUT_DATA_PREFIX = 'Input data error: '

// Deep-linkable sections. Hidden ones are excluded so `?section=` cannot reach
// a tab the sidebar no longer offers — a hidden section falls back to the
// default rather than rendering a page with no way back to it in the nav.
const ALL_SECTION_IDS: string[] = Object.values(SECTIONS).filter(
  (id) => !isSectionHidden(id)
)

/**
 * True when a "failure" is not one: the run was cancelled, or this page
 * stopped watching a run that carries on on the server (the hooks' own
 * timeout, "Still running after N minutes…"). Shown as a plain note.
 */
const isNoteNotFailure = (
  errorMessage: string | undefined,
  origin: JobFailureOrigin | undefined
) =>
  errorMessage === 'Run cancelled' ||
  (origin === 'client' && !!errorMessage?.startsWith('Still running'))

export const SchedulerInputPage = () => {
  const navigate = useNavigate()
  const { scheduleId = '', iteration = '0' } = useParams<InputPageParams>()
  const [searchParams] = useSearchParams()
  const requestedSection = searchParams.get('section')
  const [selectedSection, setSelectedSection] = useState<string>(
    requestedSection && ALL_SECTION_IDS.includes(requestedSection)
      ? requestedSection
      : SECTIONS.PRODUCTION_REQUIREMENT
  )
  // `week` and `scheduleVariant` deep-link a week and an answer — what the
  // retired output page took, so its links (Published schedules, old
  // bookmarks) land on the same thing here. Unknown values fall back; a stale
  // answer is dropped by the guard below.
  const requestedWeek = Number(searchParams.get('week'))
  const [selectedWeek, setSelectedWeek] = useState(
    Number.isInteger(requestedWeek) && requestedWeek >= 1 ? requestedWeek : 1
  )
  // Which of the run's reported answers the factory schedule shows. Lives
  // here, not in the top strip, because the strip only offers the choice —
  // the schedule data is fetched further down, by SchedulePackagingRoutesView.
  //
  // `null` = the run's own answer (the default path), which is what a plan
  // opens on: `fastest` since engine v7.176.0. Held as `null` rather than as
  // that key because the key is only known once the manifest has loaded —
  // see `ScheduleVariantSelection`.
  const [scheduleVariant, setScheduleVariant] =
    useState<ScheduleVariantSelection>(
      (searchParams.get('scheduleVariant') as ScheduleVariantKey | null) ??
        null
    )
  // Drop a selection the current run no longer offers, the same guard
  // ScheduleViewPage carries — held here rather than in the top strip because
  // this is where the selection lives, and the strip must not silently
  // disagree with the data being fetched below it.
  //
  // Two ways the selection goes stale. A re-run OVERWRITES the manifest, so
  // an answer that existed a minute ago need not exist now; and the answers a
  // run reports depend on the engine build, so an iteration re-run by an older
  // API reports three where this Portal offers four. Left unguarded the Select
  // shows a blank value while `useSchedulerData` requests a variant path that
  // was never written — a failed read, not a fallback.
  const { data: availableVariants } = useScheduleVariants(
    scheduleId,
    iteration,
    selectedWeek
  )
  // The run's own answer's key, for the picker and the own-answer checks.
  const defaultVariant = defaultVariantOf(availableVariants)
  useEffect(() => {
    if (scheduleVariant === null) return
    // Only once the list has resolved. The query is keyed by week, so changing
    // week makes this undefined mid-flight, and treating that as "the answer
    // is gone" would snap the planner back to the default on every week
    // change.
    if (!availableVariants) return
    if (!availableVariants.some((v) => v.variant_key === scheduleVariant)) {
      setScheduleVariant(null)
    }
  }, [availableVariants, scheduleVariant])

  // Original vs latest mid-week reschedule — held here, not in the schedule
  // view, because the answer picker in the top strip has to follow it.
  //
  // A reschedule reports no alternative answers (by design: they carry no
  // fixed prefix), so the answer manifest always describes the last FRESH
  // run. While a reschedule is on screen that list is about a schedule the
  // page is not showing: on 31690644102 the picker offered 170h answers over
  // a 185h rescheduled grid, and choosing "Fastest" still showed the
  // reschedule's rows. So while it is shown the picker shows the reschedule
  // alone, and the selection is held on the run's own answer.
  const [outputVariant, setOutputVariant] =
    useState<SchedulerDataVariant>('latest')
  // Pinned to the run's own answer (`null`) so this is the same request
  // whatever answer was picked — the flag is about the iteration, not the
  // answer.
  const { data: latestOutputData } = useSchedulerData(
    scheduleId,
    iteration,
    selectedWeek,
    'latest',
    null
  )
  const rescheduleShown =
    outputVariant === 'latest' &&
    Boolean(
      (latestOutputData as { rescheduled?: boolean } | undefined)?.rescheduled
    )
  const rescheduleMakespan = useMemo(() => {
    if (!rescheduleShown) return undefined
    const rows = ((latestOutputData as { data?: InputRow[] } | undefined)
      ?.data ?? []) as InputRow[]
    return computeMakespan(rows)
  }, [rescheduleShown, latestOutputData])
  useEffect(() => {
    if (rescheduleShown && scheduleVariant !== null) {
      setScheduleVariant(null)
    }
  }, [rescheduleShown, scheduleVariant])

  const [isDataEdited, setIsDataEdited] = useState(false)
  const [cfgOpen, setCfgOpen] = useState(false)
  void isDataEdited

  const queryClient = useQueryClient()

  // Once a run finishes, bring the results into view rather than leaving the
  // user wherever they triggered it from.
  const goToScheduleTab = useCallback(() => {
    setSelectedSection(SECTIONS.FACTORY_LAYOUT)
  }, [])

  // Toasts for "Run scheduler" — only what the progress banner cannot say,
  // because the banner is gone once the run ends or the page stops watching:
  //   done            → success (the banner just disappears otherwise)
  //   failed          → error with Details, held open
  //   input-data stop → error naming the row, held open
  //   cancelled, or the page stopped watching a run that carries on
  //                   → a plain note, not an error: nothing failed
  // Nothing on click (the banner appears at once and says the run carries on
  // if you leave), and nothing for a survey the API runs on past.
  const onRunSchedulerSuccess = useCallback(() => {
    toast.success('Run complete. The schedule is updated.')
    queryClient.invalidateQueries(['scheduler', scheduleId, iteration])
    queryClient.invalidateQueries(['scheduler-settings', scheduleId, iteration])
    queryClient.invalidateQueries([
      'scheduler-utilisation',
      scheduleId,
      iteration,
    ])
    goToScheduleTab()
  }, [queryClient, scheduleId, iteration, goToScheduleTab])
  const onRunSchedulerFailure = useCallback(
    (
      errorMessage?: string,
      failures?: RunFailure[],
      origin?: JobFailureOrigin
    ) => {
      if (isNoteNotFailure(errorMessage, origin)) {
        toast(errorMessage!, { duration: 8000 })
        return
      }
      toastRunFailure(
        'Why the run failed',
        errorMessage ? `Run failed: ${errorMessage}` : 'Run failed',
        failures
      )
    },
    []
  )
  // Run button → Rust scheduler_api (useRunSchedulerDirect) with live SSE
  // progress, wired into the top strip.
  const {
    mutate: runScheduler,
    isLoading: isRunning,
    progress,
    weekResults,
    lastUpdateSecs,
    resume: resumeRun,
  } = useRunSchedulerDirect(onRunSchedulerSuccess, onRunSchedulerFailure, {
    // Opening a schedule that is already running shows it running: Run
    // scheduler disabled and reading Running, with the run's progress in
    // the top strip.
    schedulerId: scheduleId,
    resume: true,
  })

  // Tenant-wide SOR settings — lowest-priority fallback.
  const { data: configSettings } = useConfigurationSettings()
  // This plan's own saved "Configure run" override, if any — must win over
  // the tenant SOR, mirroring ScheduleControlPanel.view.tsx's resolution.
  // Previously this page ran with the raw SOR settings unconditionally, so
  // saving Configure run for this plan had no effect on the actual run.
  const { data: planConfigResult } = usePlanConfigurationSettings(
    scheduleId,
    iteration
  )
  const effectiveConfigSettings = planConfigResult?.isPlanOverride
    ? planConfigResult.data
    : configSettings

  // Resolves the active multi-week consolidated selection (if applied) into
  // `consolidated_weeks` + `time_horizon`, falling back to the single
  // selected week otherwise. Both the survey and refine calls below must
  // see an identical horizon/week selection — Prepare's survey settles a
  // horizon that Run's refine-only path later trusts without re-probing
  // (see `run_scheduler_escalating_survey`'s doc comment in scheduler_rust)
  // — so `handleRunScheduler` builds this once and reuses the exact same
  // object for both requests rather than rebuilding it twice.
  const buildEnrichedRunConfig = useCallback(
    (
      config?: SchedulerRunConfig,
      weekOverride?: number
    ): SchedulerRunConfig => {
      const stored = readStoredMultiWeek(`multiWeek_${scheduleId}_${iteration}`)
      const consolidatedWeeks =
        stored?.state === 'applied' && stored.weeks.length >= 2
          ? [...stored.weeks].sort((a, b) => a - b)
          : undefined

      const tailBuffer = effectiveConfigSettings?.tail_buffer_hours ?? 400

      // Single-week: pass consolidated_weeks:[n] so the Rust API skips all other
      // weeks in the per-week loop while still using the normal per-week pipeline.
      const targetWeeks = consolidatedWeeks ?? [weekOverride ?? selectedWeek]
      const timeHorizon = consolidatedWeeks
        ? consolidatedWeeks.length * HOURS_PER_WEEK + tailBuffer
        : undefined

      return {
        ...(config ?? {}),
        consolidated_weeks: targetWeeks,
        ...(timeHorizon ? { time_horizon: timeHorizon } : {}),
      }
    },
    [scheduleId, iteration, effectiveConfigSettings, selectedWeek]
  )

  // "Run scheduler" always runs the survey phase first, then refines it —
  // there's no separate "Prepare Data" button anymore. Clicking Run submits
  // the survey with `thenRun`, and the API starts the real run itself when
  // the survey ends (or anyway, if the survey failed for a reason other than
  // the input data), handing back its job id for this page to watch. It used
  // to be this page that submitted the run, from the survey's completion
  // callback, so leaving the page during the survey meant it never ran.
  //
  // The fallback below, submitting the run from here, is for an API that
  // predates the chain and hands back no id. The enriched config is stashed
  // for it so both calls see the same horizon/week selection.
  const pendingRunConfigRef = useRef<SchedulerRunConfig | null>(null)

  const watchChainedRun = useCallback(
    (nextJobId?: string) => {
      const config = pendingRunConfigRef.current
      pendingRunConfigRef.current = null
      if (nextJobId) {
        resumeRun(nextJobId, {
          schedulerId: scheduleId,
          iteration,
          config: config ?? undefined,
        })
        return
      }
      if (!config) return
      runScheduler({ schedulerId: scheduleId, iteration, config })
    },
    [resumeRun, runScheduler, scheduleId, iteration]
  )

  const onPrepareDataSuccess = useCallback(
    (nextJobId?: string) => watchChainedRun(nextJobId),
    [watchChainedRun]
  )
  const onPrepareDataFailure = useCallback(
    (
      errorMessage?: string,
      nextJobId?: string,
      failures?: RunFailure[],
      origin?: JobFailureOrigin
    ) => {
      // Two different failures wear the same shape here, and only one of them
      // should fall through to a full run.
      //
      // A transient survey failure is worth ignoring: run-scheduler handles a
      // missing or stale prepared chromosome server-side, so the user still
      // gets a schedule. But when the INPUT DATA is what stopped it, the run
      // will fail in exactly the same way — and report it as "Demand size is
      // infeasible", which says nothing about what to change. Running on turns
      // one accurate message into two useless ones and costs a full search to
      // do it.
      //
      // The prefix is set by `PipelineError::InputData` in the API
      // (`pipeline.rs`, INPUT_DATA_PREFIX); changing it there means changing
      // it here.
      if (errorMessage?.startsWith(INPUT_DATA_PREFIX)) {
        pendingRunConfigRef.current = null
        // Held open: it names the row to fix, and a toast that vanishes in
        // four seconds is no use for that.
        toast.error(errorMessage, { duration: Infinity })
        return
      }
      // A cancel, or this PAGE giving up (it stopped watching, or the submit
      // was refused, e.g. the schedule is already running): the run is not
      // this page's to start. Running on from here used to submit a second
      // run, which the server refused with a 409 — a second red toast.
      if (origin === 'client' || errorMessage === 'Run cancelled') {
        pendingRunConfigRef.current = null
        if (isNoteNotFailure(errorMessage, origin)) {
          toast(errorMessage!, { duration: 8000 })
        } else {
          toastRunFailure(
            'Why the run could not start',
            errorMessage
              ? `The run could not start: ${errorMessage}`
              : 'The run could not start',
            failures
          )
        }
        return
      }
      // The survey failed but the API carries on with the run regardless
      // (run-scheduler handles a missing prepared chromosome), and the banner
      // moves on to it. Nothing for the planner to do, so no toast.
      console.warn(
        '[Run scheduler] survey failed; the run goes ahead without it:',
        errorMessage
      )
      watchChainedRun(nextJobId)
    },
    [watchChainedRun]
  )
  const {
    mutate: prepareData,
    isLoading: isPreparing,
    progress: prepareProgress,
    weekResults: prepareWeekResults,
    lastUpdateSecs: prepareLastUpdateSecs,
  } = useDataPrepare(onPrepareDataSuccess, onPrepareDataFailure)

  const handleRunScheduler = useCallback(
    (config?: SchedulerRunConfig, weekOverride?: number) => {
      const enriched = buildEnrichedRunConfig(config, weekOverride)
      pendingRunConfigRef.current = enriched
      prepareData({
        schedulerId: scheduleId,
        iteration,
        config: enriched,
        thenRun: true,
      })
      // No toast: the banner appears at once and itself says the run
      // carries on if you leave (client feedback 2026-10-06, which the toast
      // that was here answered for six seconds only).
    },
    [prepareData, scheduleId, iteration, buildEnrichedRunConfig]
  )

  const renderContent = () => {
    switch (selectedSection) {
      case SECTIONS.DATA_SNAPSHOTS:
        return (
          <DataSnapshotsSection
            schedulerId={scheduleId}
            iteration={iteration}
            selectedWeek={selectedWeek}
          />
        )
      case SECTIONS.COMMENTS:
        return (
          <CommentsSection
            schedulerId={scheduleId}
            iteration={iteration}
            selectedWeek={selectedWeek}
          />
        )
      case SECTIONS.FACTORY_LAYOUT:
        return (
          <SchedulePackagingRoutesView
            schedulerId={scheduleId}
            iteration={iteration}
            forceViewMode="layout"
            onDataEdited={setIsDataEdited}
            selectedWeek={selectedWeek}
            onGoToScheduleTab={goToScheduleTab}
            outputVariant={outputVariant}
            onOutputVariantChange={setOutputVariant}
          />
        )
      case SECTIONS.PRODUCTION_REQUIREMENT:
        return (
          <ScheduleProductionRequirement
            schedulerId={scheduleId}
            iteration={iteration}
          />
        )
      case SECTIONS.WORKSTATION_DOWNTIME:
        return (
          <ScheduleWorkstationDowntime
            schedulerId={scheduleId}
            iteration={iteration}
          />
        )
      case SECTIONS.PRODUCT_MASTER:
        return (
          <ScheduleProductionRequirement
            schedulerId={scheduleId}
            iteration={iteration}
            productMasterData={true}
          />
        )
      case SECTIONS.PACKAGING_ROUTES:
        return (
          <SchedulePackagingRoutesView
            schedulerId={scheduleId}
            iteration={iteration}
            forceViewMode="table"
            onDataEdited={setIsDataEdited}
            onGoToScheduleTab={goToScheduleTab}
            outputVariant={outputVariant}
            onOutputVariantChange={setOutputVariant}
          />
        )
      case SECTIONS.CLEANING_MAPPING:
        return (
          <ScheduleCleaningMapping
            schedulerId={scheduleId}
            iteration={iteration}
          />
        )
      case SECTIONS.RESOURCE_SWITCHING:
        return (
          <ScheduleResourceSwitching
            schedulerId={scheduleId}
            iteration={iteration}
          />
        )
      case SECTIONS.RESOURCE_AND_MATERIAL_CONSTRAINTS:
        return (
          <SchedulerResourceAndMaterialConstraintsView
            schedulerId={scheduleId}
            iteration={iteration}
            readOnly
            onOpenRule={setSelectedSection}
          />
        )
      // Editable here, unlike its read-only sibling above: that tab's
      // authoring moved to the Rules & Constraints workbench, and the hourly
      // profile has no home there — a 168-column grid is not a rules card.
      case SECTIONS.RESOURCE_CAPACITY_PROFILE:
        return (
          <SchedulerResourceCapacityProfileView
            schedulerId={scheduleId}
            iteration={iteration}
          />
        )
      case SECTIONS.TANKS:
        return (
          <ScheduleTanksView schedulerId={scheduleId} iteration={iteration} />
        )
      case SECTIONS.TANK_WEEK_STATE:
        return (
          <ScheduleTankWeekStateView
            schedulerId={scheduleId}
            iteration={iteration}
          />
        )
      case SECTIONS.KITCHEN_CYCLES:
        return (
          <ScheduleKitchenCyclesView
            schedulerId={scheduleId}
            iteration={iteration}
          />
        )
      case SECTIONS.RESOURCE_AND_INPUTS:
        return (
          <SchedulerResourceAndInputsView
            schedulerId={scheduleId}
            iteration={iteration}
          />
        )
      case SECTIONS.RESOURCE_AND_OUTPUT_MAPPING:
        return (
          <ScheduleResourceAndOutputMappingView
            schedulerId={scheduleId}
            iteration={iteration}
          />
        )
      case SECTIONS.PRODUCT_CLEAN_CHANGEOVER:
        return (
          <ScheduleProductCleanChangeover
            schedulerId={scheduleId}
            iteration={iteration}
            readOnly
            onOpenRule={setSelectedSection}
          />
        )
      case SECTIONS.BASE_MATERIAL_CLEAN_CHANGEOVER:
        return (
          <ScheduleBaseMaterialCleanChangeover
            schedulerId={scheduleId}
            iteration={iteration}
          />
        )
      case SECTIONS.SYNCHRONIZED_LINES:
        return (
          <ScheduleSynchronizedLines
            schedulerId={scheduleId}
            iteration={iteration}
            readOnly
            onOpenRule={setSelectedSection}
          />
        )
      case SECTIONS.STERILISER_TYPES:
        return (
          <ScheduleSORlikeView
            schedulerId={scheduleId}
            iteration={iteration}
            API="steriliser-types"
            readOnly
            onOpenRule={setSelectedSection}
          />
        )
      case SECTIONS.STERILISER_ROUTING:
        return (
          <ScheduleSteriliserRouting
            schedulerId={scheduleId}
            iteration={iteration}
            readOnly
            onOpenRule={setSelectedSection}
          />
        )
      case SECTIONS.RULES_CHANGEOVERS:
        // Unchanged for now — the changeover authoring view stays as-is.
        return (
          <ScheduleProductCleanChangeover
            schedulerId={scheduleId}
            iteration={iteration}
          />
        )
      case SECTIONS.RULES_CREW_SYNC:
        return (
          <RulesWorkbench
            schedulerId={scheduleId}
            iteration={iteration}
            initialTab="crew"
          />
        )
      case SECTIONS.RULES_STERILISER:
        return (
          <RulesWorkbench
            schedulerId={scheduleId}
            iteration={iteration}
            initialTab="steriliser"
          />
        )
      case SECTIONS.RULES_RESOURCES:
        return (
          <RulesWorkbench
            schedulerId={scheduleId}
            iteration={iteration}
            initialTab="resources"
          />
        )
      case SECTIONS.UTIL_ASSET:
        return (
          <AssetUtilisationView
            schedulerId={scheduleId}
            iteration={iteration}
          />
        )
      case SECTIONS.UTIL_DAILY:
        return (
          <DailyUtilisationView
            schedulerId={scheduleId}
            iteration={iteration}
          />
        )
      case SECTIONS.UTIL_CONSTRAINT:
        return (
          <ConstraintUtilisationView
            schedulerId={scheduleId}
            iteration={iteration}
          />
        )
      case SECTIONS.UTIL_CREW:
        return (
          <CrewUtilisationView schedulerId={scheduleId} iteration={iteration} />
        )
      case SECTIONS.UTIL_CLEANING_SUMMARY:
        return (
          <CleaningSummaryView schedulerId={scheduleId} iteration={iteration} />
        )
      case SECTIONS.UTIL_DETAILS:
        return <DetailsView schedulerId={scheduleId} iteration={iteration} />
      case SECTIONS.UTIL_FG:
        return (
          <FGUtilisationView schedulerId={scheduleId} iteration={iteration} />
        )
      case SECTIONS.UTIL_INPUT:
        return (
          <InputUtilisationView
            schedulerId={scheduleId}
            iteration={iteration}
          />
        )
      case SECTIONS.UTIL_KITCHEN:
        return (
          <KitchenUtilisationView
            schedulerId={scheduleId}
            iteration={iteration}
          />
        )
      case SECTIONS.UTIL_LINE:
        return (
          <LineUtilisationView schedulerId={scheduleId} iteration={iteration} />
        )
      default:
        return null
    }
  }

  const isRulesWorkbench =
    selectedSection === SECTIONS.RULES_CREW_SYNC ||
    selectedSection === SECTIONS.RULES_STERILISER ||
    selectedSection === SECTIONS.RULES_RESOURCES ||
    selectedSection === SECTIONS.FACTORY_LAYOUT

  // These sections render a BaseTable-backed grid (with its own title bar and,
  // for read-only sections, its own ManagedSourceBanner) — suppress the
  // page-level SchDisplay/Alert duplicates.
  const isDataTableSection = (
    [
      SECTIONS.PRODUCTION_REQUIREMENT,
      SECTIONS.WORKSTATION_DOWNTIME,
      SECTIONS.PRODUCT_MASTER,
      SECTIONS.PACKAGING_ROUTES,
      SECTIONS.CLEANING_MAPPING,
      SECTIONS.RESOURCE_AND_MATERIAL_CONSTRAINTS,
      SECTIONS.RESOURCE_CAPACITY_PROFILE,
      SECTIONS.TANKS,
      SECTIONS.TANK_WEEK_STATE,
      SECTIONS.RESOURCE_AND_INPUTS,
      SECTIONS.RESOURCE_AND_OUTPUT_MAPPING,
      SECTIONS.PRODUCT_CLEAN_CHANGEOVER,
      SECTIONS.BASE_MATERIAL_CLEAN_CHANGEOVER,
      SECTIONS.SYNCHRONIZED_LINES,
      SECTIONS.RESOURCE_SWITCHING,
      SECTIONS.STERILISER_TYPES,
      SECTIONS.STERILISER_ROUTING,
      SECTIONS.RULES_CHANGEOVERS,
    ] as string[]
  ).includes(selectedSection)

  // Suppressed while the owning rules tab is hidden: the banner's whole
  // purpose is "go and edit it there", and there is no there to go to. The
  // table's own read-only state is unaffected — see the note on HIDDEN_SECTIONS.
  const managedCandidate = !isDataTableSection
    ? RULE_MANAGED[selectedSection]
    : undefined
  const managedSource =
    managedCandidate && !isSectionHidden(managedCandidate.tab)
      ? managedCandidate
      : undefined
  const currentSectionLabel = sectionLabel(selectedSection)
  // Names the schedule in the breadcrumb. The brand bar above shows the id;
  // this is the human-readable half of the same identity.
  const { data: scheduleName = '' } = useScheduleName(scheduleId)

  return (
    // Everything below reads the planner's chosen answer through this, so the
    // factory schedule, the Gantt, the utilisation views and the KPI cards all
    // show the SAME answer instead of each defaulting to the run's own.
    <DefaultScheduleVariantContext.Provider value={defaultVariant}>
    <ScheduleVariantContext.Provider value={scheduleVariant}>
      <Fragment>
        <SchedulerThemeScope
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            // Full-bleed: the Scheduler input route renders bare (no global
            // Planner header/breadcrumb/footer — see Common/Layout Shell), so
            // this surface fills the viewport with only the design's own chrome.
            background: TOKENS.surface50,
          }}
        >
          {/* Dark-teal Scheduler brand bar. */}
          <SchedulerBrandBar
            trailing={
              <Box
                sx={{
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: 12,
                  fontFamily: FONT_MONO,
                }}
              >
                {scheduleId}
              </Box>
            }
          />

          {/* Breadcrumb — custom bar matching the design exactly
            (Scheduler/design_handoff_rules_and_constraints/AppBridgeTriage.jsx):
            back arrow · teal home crumb · "/" separators · 40px surface bar. */}
          <Box
            sx={{
              height: 40,
              flexShrink: 0,
              background: TOKENS.surface0,
              borderBottom: `1px solid ${TOKENS.line300}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
            }}
          >
            <Box
              component="button"
              title="Back to plan-scheduler home"
              onClick={() => navigate('/plan-scheduler')}
              sx={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                p: 0.5,
                borderRadius: '2px',
                color: TOKENS.ink500,
                '&:hover': { background: 'rgba(0,65,90,0.06)' },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} />
            </Box>
            <Box
              component="button"
              onClick={() => navigate('/plan-scheduler')}
              sx={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                p: 0,
                fontFamily: FONT_SANS,
                fontSize: 12.5,
                fontWeight: 600,
                color: TOKENS.brandTeal,
              }}
            >
              <HomeIcon sx={{ fontSize: 14, color: TOKENS.brandTeal }} />
              plan-scheduler
            </Box>
            <Box component="span" sx={{ color: TOKENS.ink300 }}>
              /
            </Box>
            {/* The schedule being edited. Falls back to the old static label
                until the name resolves (and if the id isn't in the list at
                all), so the bar never flashes an empty crumb. */}
            <Box
              component="button"
              title={scheduleName || undefined}
              onClick={() =>
                setSelectedSection(SECTIONS.PRODUCTION_REQUIREMENT)
              }
              sx={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                p: 0,
                fontFamily: FONT_SANS,
                fontSize: 12.5,
                fontWeight: 700,
                color: TOKENS.ink700,
                maxWidth: 320,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {scheduleName || 'Scheduler input'}
            </Box>
            <Box component="span" sx={{ color: TOKENS.ink300 }}>
              /
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: 12.5,
                color: TOKENS.ink500,
                fontFamily: FONT_SANS,
              }}
            >
              {currentSectionLabel}
            </Box>
          </Box>

          {/* Command-bridge top strip: week selector · KPIs · Configure/Run. */}
          <SchedulerTopStrip
            scheduleId={scheduleId}
            iteration={iteration}
            weeks={WEEKS}
            selectedWeek={selectedWeek}
            onSelectWeek={setSelectedWeek}
            weekResults={weekResults}
            prepareWeekResults={prepareWeekResults}
            onConfigure={() => setCfgOpen(true)}
            onRun={() =>
              handleRunScheduler(
                effectiveConfigSettings as unknown as
                  | SchedulerRunConfig
                  | undefined
              )
            }
            isRunning={isRunning}
            progress={progress}
            isPreparing={isPreparing}
            prepareProgress={prepareProgress}
            lastUpdateSecs={lastUpdateSecs}
            prepareLastUpdateSecs={prepareLastUpdateSecs}
            scheduleVariant={scheduleVariant ?? defaultVariant}
            // Picking the run's own answer goes back to `null`, so it reads
            // (and caches) the default path like a plan that was never
            // switched.
            onSelectScheduleVariant={(key) =>
              setScheduleVariant(key === defaultVariant ? null : key)
            }
            rescheduleMakespan={rescheduleMakespan}
          />

          {/* Body: sidebar + content */}
          <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
            <SchedulerInputSidebar
              selectedSection={selectedSection}
              onSelect={setSelectedSection}
              schedulerId={scheduleId}
              iteration={iteration}
            />

            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                background: TOKENS.surface50,
              }}
            >
              {isRulesWorkbench ? (
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <DeferredSection
                    sectionKey={selectedSection}
                    label={currentSectionLabel}
                  >
                    {renderContent()}
                  </DeferredSection>
                </Box>
              ) : isDataTableSection ? (
                <Box sx={{ flex: 1, minHeight: 0, p: 1.5 }}>
                  <DeferredSection
                    sectionKey={selectedSection}
                    label={currentSectionLabel}
                  >
                    {renderContent()}
                  </DeferredSection>
                </Box>
              ) : (
                <Box sx={{ px: 2.25, py: 2 }}>
                  <Box
                    sx={{
                      mb: managedSource ? 1 : 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                    }}
                  >
                    <SchDisplay>{currentSectionLabel}</SchDisplay>
                    {managedSource && (
                      <SchChip
                        tone="neutral"
                        icon={<LockIcon sx={{ fontSize: 13 }} />}
                      >
                        Read-only
                      </SchChip>
                    )}
                  </Box>

                  {managedSource && (
                    <ManagedSourceBanner
                      label={managedSource.label}
                      onOpen={() => setSelectedSection(managedSource.tab)}
                    />
                  )}
                  {/* Only the body is deferred here — the section title and
                      the read-only banner above are rendered by this page and
                      already cost nothing, so they paint with the switch and
                      the loader appears underneath them. No `label` for the
                      same reason: the heading is already on screen, and
                      "Loading X" directly under "X" reads as a stutter. The
                      other two branches are full-bleed with no heading, so
                      there the label is the only thing naming the tab. */}
                  <DeferredSection sectionKey={selectedSection}>
                    {renderContent()}
                  </DeferredSection>
                </Box>
              )}
            </Box>
          </Box>
        </SchedulerThemeScope>

        <ConfigurationSettingsDialog
          open={cfgOpen}
          onClose={() => setCfgOpen(false)}
          schedulerId={scheduleId}
          iteration={iteration}
          selectedWeek={selectedWeek}
          onWeekChange={setSelectedWeek}
        />
      </Fragment>
    </ScheduleVariantContext.Provider>
    </DefaultScheduleVariantContext.Provider>
  )
}
